import { isDocumentRunning } from "pages/DevelopWorkspace/helpers/isDocumentRunning";
import {
  DocumentsState,
  QueryStatementStatus,
} from "pages/DevelopWorkspace/workspace.types";

import { documentHasErrors } from "../../../helpers/documentHasError";
import { isTabHidden } from "./tabGroupOrdering";

const checkIfActiveDocumentIdValid = (
  state: DocumentsState,
  normalizedState: DocumentsState
) => {
  const document = state.documents.find(
    document => document.id === state.activeDocumentId
  );

  if (!document) {
    // set to first document
    if (state.documents.length > 0) {
      // eslint-disable-next-line no-param-reassign -- don't want to create new object
      normalizedState.activeDocumentId = state.documents[0].id;
    } else {
      // eslint-disable-next-line no-param-reassign -- don't want to create new object
      normalizedState.activeDocumentId = null;
    }
  }
};

const checkActiveQueryStatementIndex = (
  state: DocumentsState,
  normalizedState: DocumentsState
) => {
  state.documents.forEach(document => {
    if (!document.execution) {
      return;
    }

    const queryStatements = document.execution.queryStatements;

    if (
      document.execution.activeQueryStatementIndex >= queryStatements.length
    ) {
      // eslint-disable-next-line no-param-reassign -- don't want to create new object
      normalizedState.documents.find(
        normalizedDocument => normalizedDocument.id === document.id
      )!.execution!.activeQueryStatementIndex = 0;
    }
  });
};

const normalizeCancelledQueryStatements = (
  state: DocumentsState,
  normalizedState: DocumentsState
) => {
  state.documents.forEach(document => {
    if (!document.execution) {
      return;
    }

    const queryStatements = document.execution.queryStatements;

    const hasError = documentHasErrors(document);

    if (hasError) {
      // at least one query statement has failed
      const normalizedQueryStatements = queryStatements.map(queryStatement => {
        if (queryStatement.status === QueryStatementStatus.pending) {
          return {
            ...queryStatement,
            status: QueryStatementStatus.cancelled,
          };
        }

        return queryStatement;
      });

      // eslint-disable-next-line no-param-reassign -- don't want to create new object
      normalizedState.documents.find(
        normalizedDocument => normalizedDocument.id === document.id
      )!.execution!.queryStatements = normalizedQueryStatements;
    }
  });
};

const normalizeDocumentCancellationStatus = (
  state: DocumentsState,
  normalizedState: DocumentsState
) => {
  state.documents.forEach(document => {
    if (!document.execution) {
      return;
    }

    // reset cancellation status if document is not running
    if (isDocumentRunning(document)) {
      return;
    }

    // eslint-disable-next-line no-param-reassign -- don't want to create new object
    normalizedState.documents.find(
      normalizedDocument => normalizedDocument.id === document.id
    )!.execution!.cancellationStatus = undefined;
  });
};

/*
  A group exists only as long as it holds a tab, so closing or moving out the last tab
  deletes the group.
 */
const pruneEmptyTabGroups = (normalizedState: DocumentsState) => {
  const tabGroups = normalizedState.tabGroups.filter(tabGroup =>
    normalizedState.documents.some(
      document => document.groupId === tabGroup.id
    )
  );

  if (tabGroups.length !== normalizedState.tabGroups.length) {
    // eslint-disable-next-line no-param-reassign -- don't want to create new object
    normalizedState.tabGroups = tabGroups;
  }
};

/*
  The active tab can never sit inside a collapsed group as the group hides the tab
  while the editor renders the active document independently, so nothing in the bar
  is highlighted.
  By the time we get here a handler has already picked the active tab, so we must
  expand its group here (for each handler that can collapse a group, close a tab or
  change the active tab). This is applied to restored state too.
*/
const expandActiveTabGroup = (state: DocumentsState): DocumentsState => {
  const activeDocument = state.documents.find(
    document => document.id === state.activeDocumentId
  );

  if (!activeDocument || !isTabHidden(state.tabGroups, activeDocument)) {
    return state;
  }

  return {
    ...state,
    tabGroups: state.tabGroups.map(tabGroup =>
      tabGroup.id === activeDocument.groupId
        ? { ...tabGroup, collapsed: false }
        : tabGroup
    ),
  };
};

const normalizeState = (state: DocumentsState): DocumentsState => {
  const normalizedState = { ...state };

  // cancel the rest of the query statements if one of them has failed (status = error)
  // TODO not sure that it is the best place for this logic
  normalizeCancelledQueryStatements(state, normalizedState);

  checkIfActiveDocumentIdValid(state, normalizedState);

  // check if valid activeQueryStatementIndex, otherwise set to null
  checkActiveQueryStatementIndex(state, normalizedState);

  // reset cancellation status if document is not running
  normalizeDocumentCancellationStatus(state, normalizedState);

  // delete tab groups with no tabs
  pruneEmptyTabGroups(normalizedState);

  return expandActiveTabGroup(normalizedState);
};

export { expandActiveTabGroup, normalizeState };
